/**
 * URLs d’affichage des pièces jointes (miniatures carte + formulaire).
 * Règle : /api/... reste en chemin relatif (même origine que la page / proxy Vercel).
 * Seuls /uploads/... peuvent être préfixés par l’origine backend publique.
 */
import { getApiPublicOrigin } from '../services/api';

export const normalizeReportIdKey = (id) => {
  if (id == null || id === '') return '';
  return String(id);
};

export const readAuthTokenForAsset = () => {
  try {
    const fromLocal = localStorage.getItem('token');
    if (fromLocal) return fromLocal;
  } catch (_) {
    /* ignore */
  }
  try {
    const fromSession = sessionStorage.getItem('token');
    if (fromSession) return fromSession;
  } catch (_) {
    /* ignore */
  }
  return '';
};

export const appendAuthTokenToApiAssetUrl = (urlValue) => {
  const raw = String(urlValue || '').trim();
  if (!raw) return raw;
  if (!/\/api\//i.test(raw) || /[?&]token=/i.test(raw)) return raw;
  const token = readAuthTokenForAsset();
  if (!token) return raw;
  const separator = raw.includes('?') ? '&' : '?';
  return `${raw}${separator}token=${encodeURIComponent(token)}`;
};

export const appendStrictFallbackParam = (urlValue) => {
  const raw = String(urlValue || '').trim();
  if (!raw || !/\/api\/intervention-reports\//i.test(raw)) return raw;
  if (/[?&]fallback=/.test(raw)) return raw;
  const separator = raw.includes('?') ? '&' : '?';
  return `${raw}${separator}fallback=1`;
};

export const encodePathSegments = (pathStr) => {
  const n = String(pathStr || '').replace(/\\/g, '/').trim();
  if (!n.startsWith('/')) return encodeURIComponent(n);
  const parts = n.split('/').filter((p) => p.length > 0);
  return `/${parts
    .map((p) => {
      try {
        return encodeURIComponent(decodeURIComponent(p));
      } catch (_) {
        return encodeURIComponent(p);
      }
    })
    .join('/')}`;
};

/** Nom de fichier seul (segment by-filename Express). */
export const encodeAttachmentFilename = (filename) => {
  const leaf = String(filename || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
    .pop() || '';
  if (!leaf) return '';
  try {
    return encodeURIComponent(decodeURIComponent(leaf));
  } catch (_) {
    return encodeURIComponent(leaf);
  }
};

const extractAttachmentBaseName = (value) => {
  const normalized = String(value || '').replace(/\\/g, '/').trim();
  if (!normalized) return '';
  const parts = normalized.split('/').filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : normalized;
};

/**
 * Chemins candidats relatifs (sans origine) — ordre aligné formulaire / API.
 */
export const buildInterventionReportAttachmentCandidates = (reportId, file) => {
  if (!file || typeof file !== 'object') return [];
  const out = [];
  const push = (value) => {
    const candidate = String(value || '').trim();
    if (!candidate || out.includes(candidate)) return;
    out.push(candidate);
  };

  push(file.api_url);

  const reportKey = normalizeReportIdKey(reportId);
  if (reportKey && file.id != null && String(file.id).trim() !== '') {
    push(`/api/intervention-reports/${reportKey}/images/${file.id}/content`);
  }

  const baseName = extractAttachmentBaseName(file.file_path || file.filename || file.original_name);
  const filename = String(file.filename || file.original_name || baseName || '').trim();
  if (filename && reportKey) {
    push(
      `/api/intervention-reports/${reportKey}/images/by-filename/${encodeAttachmentFilename(filename)}/content`
    );
  }

  push(file.file_path);
  push(file.fichier_pdf_url);
  push(file.url);
  push(file.path);

  if (filename) {
    push(`/uploads/rapports/${filename}`);
    push(`/uploads/intervention-reports/${filename}`);
    push(`/uploads/pdfs/${filename}`);
  }

  return out;
};

/**
 * Une URL d’affichage par candidat — même logique que InterventionReportForm (formulaire OK).
 * - /api/... : chemin relatif + token (+ fallback) — jamais préfixé par API_PUBLIC_ORIGIN
 * - /uploads/... : chemin relatif en navigateur (rewrite Vercel) ; pas de préfixe Render pour les <img>
 */
export const buildInterventionReportDisplayCandidates = (reportId, file) => {
  const apiOrigin = getApiPublicOrigin();
  return buildInterventionReportAttachmentCandidates(reportId, file).map((candidate) => {
    let path = String(candidate || '').trim().replace(/\\/g, '/');
    if (!path) return '';
    path = path.replace(/^https?:\/\/[^/]+/i, '');
    if (!path.startsWith('/')) path = `/${path}`;

    if (path.startsWith('/api/')) {
      return appendAuthTokenToApiAssetUrl(appendStrictFallbackParam(path));
    }

    const pathEnc = encodePathSegments(path);
    if (apiOrigin) {
      return `${apiOrigin}${pathEnc}`;
    }
    return pathEnc;
  }).filter(Boolean);
};

/** Une seule URL (hors liste de repli). */
export const resolveAssetUrlForDisplay = (assetPath) => {
  const raw = String(assetPath || '').trim();
  if (!raw) return null;
  let path = raw.replace(/\\/g, '/').replace(/^https?:\/\/[^/]+/i, '');
  if (!path.startsWith('/')) path = `/${path}`;
  const apiOrigin = getApiPublicOrigin();
  if (path.startsWith('/api/')) {
    return appendAuthTokenToApiAssetUrl(appendStrictFallbackParam(path));
  }
  const pathEnc = encodePathSegments(path);
  if (path.startsWith('/uploads/') && typeof window !== 'undefined') {
    return pathEnc;
  }
  if (apiOrigin) return `${apiOrigin}${pathEnc}`;
  return pathEnc;
};

/** Chemin axios (baseURL /api) depuis une URL d’affichage — repli blob authentifié. */
export const toAxiosApiPathFromDisplayUrl = (displayUrl) => {
  let path = String(displayUrl || '').trim();
  if (!path) return '';
  path = path.replace(/^https?:\/\/[^/]+/i, '');
  if (path.startsWith('/api/')) path = path.slice(4);
  if (!path.startsWith('/')) path = `/${path}`;
  const qIndex = path.indexOf('?');
  const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const search = qIndex >= 0 ? path.slice(qIndex + 1) : '';
  const params = new URLSearchParams(search);
  params.delete('token');
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
};

export const extractActivityReportPdfPath = (payload) => {
  if (!payload || typeof payload !== 'object') return '';
  const candidates = [
    payload.pdf_url,
    payload.pdfUrl,
    payload.fichier_pdf_url,
    payload.file_path,
    payload.data?.pdf_url,
    payload.data?.fichier_pdf_url,
    payload.report?.fichier_pdf_url,
    payload.report?.file_path
  ];
  for (const value of candidates) {
    const trimmed = String(value || '').trim();
    if (trimmed) return trimmed;
  }
  return '';
};
